import type { SimpleFieldConfig } from "@/components/shared/UniversalCRMTable/types";
import type { BaseEntity } from "@/components/shared/UniversalCRMTable/types/base-types";
import { extractFriendlyName } from "../../relation/relation-helpers";
import { asDataItem, getGroupingValue } from "./helpers";
import type { DateGroupingGranularity, GroupedItem } from "./types";

/** `YYYY-MM-DD` — what a Postgres `date` column and an `<input type="date">` produce. */
const DATE_ONLY_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Parse a cell value into the date the row should be GROUPED under.
 *
 * A date-only string carries no time and no zone, so `new Date("2026-03-01")`
 * is UTC midnight — which in any negative-offset zone (America/Chicago) reads
 * back as Feb 28 local. Grouping by a `date` column that way files a deal that
 * closes on the 1st under the PREVIOUS month. Date-only values are therefore
 * read as a local calendar day; values that carry a real instant are not
 * touched. See WTC_DEFAULT_TIMEZONE.
 */
function parseGroupingDate(raw: unknown): Date | null {
  if (raw instanceof Date) return Number.isNaN(raw.getTime()) ? null : raw;

  if (typeof raw === "string" && DATE_ONLY_PATTERN.test(raw)) {
    const [year, month, day] = raw.split("-").map(Number);
    const local = new Date(year, month - 1, day);
    return Number.isNaN(local.getTime()) ? null : local;
  }

  if (typeof raw === "string" || typeof raw === "number") {
    const parsed = new Date(raw);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }

  return null;
}

export function groupByEnum<T extends BaseEntity>(
  data: T[],
  fieldId: string,
  fieldConfig: SimpleFieldConfig
): GroupedItem<T>[] {
  const grouped = new Map<string, T[]>();
  const valueToLabel = new Map<string, string>();

  if (fieldConfig.options) {
    fieldConfig.options.forEach((opt) => {
      valueToLabel.set(String(opt.value), opt.label);
    });
  }

  data.forEach((item) => {
    const value = getGroupingValue(item, fieldId);
    let key: string;
    if (typeof value === "object" && value !== null && (value as Record<string, unknown>).id) {
      key = extractFriendlyName(value, fieldId) || String((value as Record<string, unknown>).id);
    } else {
      key = String(value ?? "");
    }
    if (!grouped.has(key)) grouped.set(key, []);
    grouped.get(key)!.push(item);
  });

  const result: GroupedItem<T>[] = [];
  grouped.forEach((items, key) => {
    let label = valueToLabel.get(key) || key;

    if (items.length > 0) {
      const value = getGroupingValue(items[0], fieldId);
      if (typeof value === "object" && value !== null && (value as Record<string, unknown>).id) {
        const friendlyName = extractFriendlyName(value, fieldId);
        if (friendlyName) label = friendlyName;
      }
    }

    if (!key || key === "" || key === "null" || key === "undefined") {
      label = "(No Value)";
    }

    const normalizedKey = key || "no-value";
    result.push({
      type: "group-header",
      id: `group-${fieldId}-${normalizedKey}`,
      groupKey: normalizedKey,
      groupLabel: label,
      groupValue: key,
      itemCount: items.length,
    });
    items.forEach((item) =>
      result.push(asDataItem(item, { type: "data", groupKey: normalizedKey, groupLabel: label }))
    );
  });

  return result;
}

export function groupByMultiUser<T extends BaseEntity>(
  data: T[],
  fieldId: string,
  _fieldConfig: SimpleFieldConfig
): GroupedItem<T>[] {
  const grouped = new Map<string, T[]>();

  data.forEach((item) => {
    const raw = item[fieldId];
    const userArray: string[] = Array.isArray(raw) ? (raw as string[]) : raw ? [String(raw)] : [];

    if (userArray.length === 0) {
      if (!grouped.has("unassigned")) grouped.set("unassigned", []);
      grouped.get("unassigned")!.push(item);
    } else {
      userArray.forEach((userId) => {
        if (!grouped.has(userId)) grouped.set(userId, []);
        grouped.get(userId)!.push(item);
      });
    }
  });

  const result: GroupedItem<T>[] = [];
  grouped.forEach((items, key) => {
    const label = key === "unassigned" ? "Unassigned" : key;
    result.push({
      type: "group-header",
      id: `group-${fieldId}-${key}`,
      groupKey: key,
      groupLabel: label,
      groupValue: key,
      itemCount: items.length,
    });
    items.forEach((item) =>
      result.push(asDataItem(item, { type: "data", groupKey: key, groupLabel: label }))
    );
  });

  return result;
}

export function groupByBoolean<T extends BaseEntity>(
  data: T[],
  fieldId: string,
  _fieldConfig: SimpleFieldConfig
): GroupedItem<T>[] {
  const grouped = new Map<string, T[]>();

  data.forEach((item) => {
    const value = item[fieldId];
    const key = value === null || value === undefined ? "null" : value ? "true" : "false";
    if (!grouped.has(key)) grouped.set(key, []);
    grouped.get(key)!.push(item);
  });

  const result: GroupedItem<T>[] = [];
  const order = ["true", "false", "null"];
  order.forEach((key) => {
    const items = grouped.get(key);
    if (!items) return;
    const label = key === "true" ? "Yes" : key === "false" ? "No" : "(No Value)";
    const groupValue: boolean | undefined =
      key === "true" ? true : key === "false" ? false : undefined;
    result.push({
      type: "group-header",
      id: `group-${fieldId}-${key}`,
      groupKey: key,
      groupLabel: label,
      groupValue,
      itemCount: items.length,
    });
    items.forEach((item) =>
      result.push(asDataItem(item, { type: "data", groupKey: key, groupLabel: label }))
    );
  });

  return result;
}

export function groupByDate<T extends BaseEntity>(
  data: T[],
  fieldId: string,
  _fieldConfig: SimpleFieldConfig,
  granularity: DateGroupingGranularity = "month"
): GroupedItem<T>[] {
  const grouped = new Map<string, T[]>();
  const keyToLabel = new Map<string, string>();

  const getDateKey = (raw: unknown): { key: string; label: string } => {
    if (!raw) return { key: "no-date", label: "No Date" };
    const date = parseGroupingDate(raw);
    if (!date) return { key: "no-date", label: "No Date" };
    if (granularity === "day") {
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, "0");
      const d = String(date.getDate()).padStart(2, "0");
      return {
        key: `${y}-${m}-${d}`,
        label: date.toLocaleDateString("en-US", {
          year: "numeric",
          month: "numeric",
          day: "numeric",
        }),
      };
    } else if (granularity === "month") {
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, "0");
      return {
        key: `${y}-${m}`,
        label: date.toLocaleDateString("en-US", { year: "numeric", month: "long" }),
      };
    } else {
      const y = `${date.getFullYear()}`;
      return { key: y, label: y };
    }
  };

  data.forEach((item) => {
    const { key, label } = getDateKey(item[fieldId]);
    if (!grouped.has(key)) grouped.set(key, []);
    if (!keyToLabel.has(key)) keyToLabel.set(key, label);
    grouped.get(key)!.push(item);
  });

  const result: GroupedItem<T>[] = [];
  Array.from(grouped.keys())
    .sort()
    .reverse()
    .forEach((key) => {
      const items = grouped.get(key)!;
      const label = keyToLabel.get(key) ?? key;
      result.push({
        type: "group-header",
        id: `group-${fieldId}-${key}`,
        groupKey: key,
        groupLabel: label,
        groupValue: key,
        itemCount: items.length,
      });
      items.forEach((item) => result.push(asDataItem(item, { type: "data", groupKey: key })));
    });

  return result;
}
